/**
 * Payroll Service
 * Handles payroll calculations for church employees
 * Ghana-specific: SSNIT, PAYE, Tier 2/3 pensions
 */

import { supabase } from '@/lib/supabase'

// ============================================
// TYPES & INTERFACES
// ============================================

export interface Employee {
  id: string
  church_id: string
  employee_code: string
  first_name: string
  last_name: string
  department: string
  position_title: string
  employment_type: string
  basic_salary: number
  tin?: string
  ssnit_number?: string
  bank_name?: string
  account_number?: string
  is_active: boolean
}

export interface SalaryComponent {
  id: string
  employee_id: string
  component_type: 'allowance' | 'deduction'
  component_code: string
  component_name: string
  amount: number
  calculation_type: 'fixed' | 'percentage'
  percentage_basis?: string
  is_taxable: boolean
  is_pensionable: boolean
  is_active: boolean
}

export interface PayrollRun {
  id: string
  church_id: string
  payroll_period_start: string
  payroll_period_end: string
  payroll_month: number
  payroll_year: number
  payment_date?: string
  status: 'draft' | 'calculated' | 'approved' | 'paid' | 'posted'
  total_employees: number
  total_gross: number
  total_deductions: number
  total_net_pay: number
  total_employer_ssnit: number
  total_paye: number
}

export interface PayrollDetail {
  id?: string
  payroll_run_id: string
  employee_id: string
  employee_code: string
  employee_name: string
  department: string
  position_title: string

  // Earnings
  basic_salary: number
  housing_allowance: number
  transport_allowance: number
  utility_allowance: number
  meal_allowance: number
  other_allowances: number
  overtime: number
  bonus: number
  gross_pay: number
  taxable_income: number

  // Statutory deductions
  employee_ssnit: number
  employer_ssnit: number
  paye_tax: number
  tier2_employee: number
  tier2_employer: number

  // Other deductions
  loan_deduction: number
  other_deductions: number
  total_deductions: number

  // Net pay
  net_pay: number
  employer_total_cost: number
}

// ============================================
// GHANA TAX CONSTANTS (2024/2025)
// ============================================

export const GHANA_TAX_RATES = {
  // SSNIT Rates (Social Security)
  SSNIT_EMPLOYEE_RATE: 0.055, // 5.5% of basic salary
  SSNIT_EMPLOYER_RATE: 0.13,  // 13% of basic salary

  // Tier 2 Pension (Mandatory occupational pension)
  TIER2_EMPLOYEE_RATE: 0.05,  // 5% of basic salary
  TIER2_EMPLOYER_RATE: 0.05,  // 5% of basic salary (optional)

  // PAYE Tax Brackets (Annual)
  PAYE_BRACKETS: [
    { min: 0, max: 4380, rate: 0 },           // First GHS 4,380 - 0%
    { min: 4380, max: 6380, rate: 0.05 },     // Next GHS 2,000 - 5%
    { min: 6380, max: 34380, rate: 0.10 },    // Next GHS 28,000 - 10%
    { min: 34380, max: 50380, rate: 0.175 },  // Next GHS 16,000 - 17.5%
    { min: 50380, max: 254380, rate: 0.25 },  // Next GHS 204,000 - 25%
    { min: 254380, max: Infinity, rate: 0.30 } // Above GHS 254,380 - 30%
  ],

  // Annual Tax Relief
  ANNUAL_TAX_RELIEF: 1200, // GHS 1,200 per year
}

// ============================================
// PAYROLL CALCULATION SERVICE
// ============================================

export class PayrollService {

  /**
   * Calculate SSNIT contributions
   * Employee: 5.5% of basic salary
   * Employer: 13% of basic salary
   */
  static calculateSSNIT(basicSalary: number): {
    employee: number
    employer: number
    total: number
  } {
    const employee = basicSalary * GHANA_TAX_RATES.SSNIT_EMPLOYEE_RATE
    const employer = basicSalary * GHANA_TAX_RATES.SSNIT_EMPLOYER_RATE

    return {
      employee: Math.round(employee * 100) / 100,
      employer: Math.round(employer * 100) / 100,
      total: Math.round((employee + employer) * 100) / 100
    }
  }

  /**
   * Calculate Tier 2 pension contributions
   * Employee: 5% of basic salary
   * Employer: 5% of basic salary (optional)
   */
  static calculateTier2(basicSalary: number, employerContributes: boolean = true): {
    employee: number
    employer: number
    total: number
  } {
    const employee = basicSalary * GHANA_TAX_RATES.TIER2_EMPLOYEE_RATE
    const employer = employerContributes ? basicSalary * GHANA_TAX_RATES.TIER2_EMPLOYER_RATE : 0

    return {
      employee: Math.round(employee * 100) / 100,
      employer: Math.round(employer * 100) / 100,
      total: Math.round((employee + employer) * 100) / 100
    }
  }

  /**
   * Calculate PAYE (income tax) using Ghana progressive tax rates
   * Based on annual taxable income
   */
  static calculatePAYE(monthlyTaxableIncome: number): {
    monthlyPaye: number
    annualTaxableIncome: number
    annualPaye: number
    effectiveRate: number
  } {
    const annualTaxableIncome = monthlyTaxableIncome * 12
    let annualPaye = 0

    // Apply progressive tax brackets
    for (const bracket of GHANA_TAX_RATES.PAYE_BRACKETS) {
      if (annualTaxableIncome > bracket.min) {
        const taxableInBracket = Math.min(
          annualTaxableIncome - bracket.min,
          bracket.max - bracket.min
        )
        annualPaye += taxableInBracket * bracket.rate
      }
    }

    // Deduct annual tax relief
    annualPaye = Math.max(0, annualPaye - GHANA_TAX_RATES.ANNUAL_TAX_RELIEF)

    const monthlyPaye = annualPaye / 12
    const effectiveRate = annualTaxableIncome > 0 ? (annualPaye / annualTaxableIncome) * 100 : 0

    return {
      monthlyPaye: Math.round(monthlyPaye * 100) / 100,
      annualTaxableIncome: Math.round(annualTaxableIncome * 100) / 100,
      annualPaye: Math.round(annualPaye * 100) / 100,
      effectiveRate: Math.round(effectiveRate * 100) / 100
    }
  }

  /**
   * Calculate total allowances for an employee
   */
  static async calculateAllowances(
    employeeId: string,
    basicSalary: number
  ): Promise<{
    housing: number
    transport: number
    utility: number
    meal: number
    other: number
    total: number
    taxableTotal: number
  }> {
    const { data: components } = await supabase
      .from('employee_salary_components')
      .select('*')
      .eq('employee_id', employeeId)
      .eq('component_type', 'allowance')
      .eq('is_active', true)

    const allowances = {
      housing: 0,
      transport: 0,
      utility: 0,
      meal: 0,
      other: 0,
      total: 0,
      taxableTotal: 0
    }

    if (components) {
      for (const comp of components) {
        let amount = comp.amount

        // Calculate percentage-based allowances
        if (comp.calculation_type === 'percentage') {
          const basis = comp.percentage_basis === 'basic_salary' ? basicSalary : 0
          amount = (basis * comp.amount) / 100
        }

        // Categorize allowances
        if (comp.component_code.includes('housing')) {
          allowances.housing += amount
        } else if (comp.component_code.includes('transport')) {
          allowances.transport += amount
        } else if (comp.component_code.includes('utility')) {
          allowances.utility += amount
        } else if (comp.component_code.includes('meal')) {
          allowances.meal += amount
        } else {
          allowances.other += amount
        }

        allowances.total += amount

        if (comp.is_taxable) {
          allowances.taxableTotal += amount
        }
      }
    }

    return {
      housing: Math.round(allowances.housing * 100) / 100,
      transport: Math.round(allowances.transport * 100) / 100,
      utility: Math.round(allowances.utility * 100) / 100,
      meal: Math.round(allowances.meal * 100) / 100,
      other: Math.round(allowances.other * 100) / 100,
      total: Math.round(allowances.total * 100) / 100,
      taxableTotal: Math.round(allowances.taxableTotal * 100) / 100
    }
  }

  /**
   * Calculate other deductions for an employee
   */
  static async calculateOtherDeductions(employeeId: string, basicSalary: number): Promise<{
    loan: number
    other: number
    total: number
  }> {
    const { data: components } = await supabase
      .from('employee_salary_components')
      .select('*')
      .eq('employee_id', employeeId)
      .eq('component_type', 'deduction')
      .eq('is_active', true)

    const deductions = {
      loan: 0,
      other: 0,
      total: 0
    }

    if (components) {
      for (const comp of components) {
        let amount = comp.amount

        // Calculate percentage-based deductions
        if (comp.calculation_type === 'percentage') {
          const basis = comp.percentage_basis === 'basic_salary' ? basicSalary : 0
          amount = (basis * comp.amount) / 100
        }

        if (comp.component_code.includes('loan')) {
          deductions.loan += amount
        } else {
          deductions.other += amount
        }

        deductions.total += amount
      }
    }

    return {
      loan: Math.round(deductions.loan * 100) / 100,
      other: Math.round(deductions.other * 100) / 100,
      total: Math.round(deductions.total * 100) / 100
    }
  }

  /**
   * Calculate complete payroll for a single employee
   */
  static async calculateEmployeePayroll(
    employee: Employee,
    payrollRunId: string,
    churchId: string,
    bonusAmount: number = 0,
    overtimeAmount: number = 0
  ): Promise<PayrollDetail> {

    const basicSalary = employee.basic_salary

    // Calculate allowances
    const allowances = await this.calculateAllowances(employee.id, basicSalary)

    // Calculate gross pay
    const grossPay = basicSalary + allowances.total + bonusAmount + overtimeAmount

    // Calculate SSNIT (on basic salary only)
    const ssnit = this.calculateSSNIT(basicSalary)

    // Calculate taxable income (gross - non-taxable allowances - employee SSNIT)
    const taxableIncome = grossPay - (allowances.total - allowances.taxableTotal) - ssnit.employee

    // Calculate PAYE
    const paye = this.calculatePAYE(taxableIncome)

    // Calculate Tier 2
    const tier2 = this.calculateTier2(basicSalary)

    // Calculate other deductions
    const otherDeductions = await this.calculateOtherDeductions(employee.id, basicSalary)

    // Calculate total deductions
    const totalDeductions =
      ssnit.employee +
      paye.monthlyPaye +
      tier2.employee +
      otherDeductions.total

    // Calculate net pay
    const netPay = grossPay - totalDeductions

    // Calculate employer total cost
    const employerTotalCost = grossPay + ssnit.employer + tier2.employer

    return {
      payroll_run_id: payrollRunId,
      employee_id: employee.id,
      employee_code: employee.employee_code,
      employee_name: `${employee.first_name} ${employee.last_name}`,
      department: employee.department || '',
      position_title: employee.position_title,

      // Earnings
      basic_salary: basicSalary,
      housing_allowance: allowances.housing,
      transport_allowance: allowances.transport,
      utility_allowance: allowances.utility,
      meal_allowance: allowances.meal,
      other_allowances: allowances.other,
      overtime: overtimeAmount,
      bonus: bonusAmount,
      gross_pay: Math.round(grossPay * 100) / 100,
      taxable_income: Math.round(taxableIncome * 100) / 100,

      // Statutory deductions
      employee_ssnit: ssnit.employee,
      employer_ssnit: ssnit.employer,
      paye_tax: paye.monthlyPaye,
      tier2_employee: tier2.employee,
      tier2_employer: tier2.employer,

      // Other deductions
      loan_deduction: otherDeductions.loan,
      other_deductions: otherDeductions.other,
      total_deductions: Math.round(totalDeductions * 100) / 100,

      // Net pay
      net_pay: Math.round(netPay * 100) / 100,
      employer_total_cost: Math.round(employerTotalCost * 100) / 100
    }
  }

  /**
   * Create a new payroll run for a specific month
   */
  static async createPayrollRun(
    churchId: string,
    month: number,
    year: number,
    userId: string
  ): Promise<{ success: boolean; payrollRunId?: string; error?: string }> {
    try {
      // Check if payroll already exists for this period
      const { data: existing } = await supabase
        .from('payroll_runs')
        .select('id')
        .eq('church_id', churchId)
        .eq('payroll_month', month)
        .eq('payroll_year', year)
        .single()

      if (existing) {
        return {
          success: false,
          error: `Payroll already exists for ${year}-${month.toString().padStart(2, '0')}`
        }
      }

      // Calculate period dates
      const periodStart = new Date(year, month - 1, 1)
      const periodEnd = new Date(year, month, 0)

      // Create payroll run
      const { data: payrollRun, error } = await supabase
        .from('payroll_runs')
        .insert({
          church_id: churchId,
          payroll_period_start: periodStart.toISOString().split('T')[0],
          payroll_period_end: periodEnd.toISOString().split('T')[0],
          payroll_month: month,
          payroll_year: year,
          status: 'draft',
          created_by: userId
        })
        .select()
        .single()

      if (error) throw error

      return {
        success: true,
        payrollRunId: payrollRun.id
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Calculate payroll for all active employees
   */
  static async calculatePayrollForAllEmployees(
    payrollRunId: string,
    churchId: string
  ): Promise<{ success: boolean; employeesProcessed: number; error?: string }> {
    try {
      // Get all active employees
      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('*')
        .eq('church_id', churchId)
        .eq('employment_status', 'active')
        .eq('is_active', true)

      if (empError) throw empError
      if (!employees || employees.length === 0) {
        return { success: false, employeesProcessed: 0, error: 'No active employees found' }
      }

      // Calculate payroll for each employee
      const payrollDetails: PayrollDetail[] = []

      for (const employee of employees) {
        const detail = await this.calculateEmployeePayroll(
          employee,
          payrollRunId,
          churchId
        )
        payrollDetails.push(detail)
      }

      // Insert all payroll details
      const { error: insertError } = await supabase
        .from('payroll_details')
        .insert(payrollDetails.map(pd => ({
          ...pd,
          church_id: churchId
        })))

      if (insertError) throw insertError

      // Update payroll run totals
      const totals = payrollDetails.reduce((acc, pd) => ({
        total_employees: acc.total_employees + 1,
        total_basic_salary: acc.total_basic_salary + pd.basic_salary,
        total_allowances: acc.total_allowances + (pd.housing_allowance + pd.transport_allowance + pd.utility_allowance + pd.meal_allowance + pd.other_allowances),
        total_gross: acc.total_gross + pd.gross_pay,
        total_employee_ssnit: acc.total_employee_ssnit + pd.employee_ssnit,
        total_employer_ssnit: acc.total_employer_ssnit + pd.employer_ssnit,
        total_paye: acc.total_paye + pd.paye_tax,
        total_tier2_employee: acc.total_tier2_employee + pd.tier2_employee,
        total_tier2_employer: acc.total_tier2_employer + pd.tier2_employer,
        total_other_deductions: acc.total_other_deductions + (pd.loan_deduction + pd.other_deductions),
        total_deductions: acc.total_deductions + pd.total_deductions,
        total_net_pay: acc.total_net_pay + pd.net_pay
      }), {
        total_employees: 0,
        total_basic_salary: 0,
        total_allowances: 0,
        total_gross: 0,
        total_employee_ssnit: 0,
        total_employer_ssnit: 0,
        total_paye: 0,
        total_tier2_employee: 0,
        total_tier2_employer: 0,
        total_other_deductions: 0,
        total_deductions: 0,
        total_net_pay: 0
      })

      const { error: updateError } = await supabase
        .from('payroll_runs')
        .update({
          ...totals,
          status: 'calculated',
          calculated_at: new Date().toISOString()
        })
        .eq('id', payrollRunId)

      if (updateError) throw updateError

      return {
        success: true,
        employeesProcessed: employees.length
      }
    } catch (error: any) {
      return {
        success: false,
        employeesProcessed: 0,
        error: error.message
      }
    }
  }

  /**
   * Get payroll run by ID
   */
  static async getPayrollRun(payrollRunId: string): Promise<PayrollRun | null> {
    const { data, error } = await supabase
      .from('payroll_runs')
      .select('*')
      .eq('id', payrollRunId)
      .single()

    if (error || !data) return null
    return data
  }

  /**
   * Get payroll details for a run
   */
  static async getPayrollDetails(payrollRunId: string): Promise<PayrollDetail[]> {
    const { data, error } = await supabase
      .from('payroll_details')
      .select('*')
      .eq('payroll_run_id', payrollRunId)
      .order('employee_code')

    if (error || !data) return []
    return data
  }

  /**
   * Get all payroll runs for a church
   */
  static async getPayrollRuns(
    churchId: string,
    year?: number
  ): Promise<PayrollRun[]> {
    let query = supabase
      .from('payroll_runs')
      .select('*')
      .eq('church_id', churchId)

    if (year) {
      query = query.eq('payroll_year', year)
    }

    const { data, error } = await query.order('payroll_year', { ascending: false })
      .order('payroll_month', { ascending: false })

    if (error || !data) return []
    return data
  }

  /**
   * Approve payroll run
   */
  static async approvePayrollRun(
    payrollRunId: string,
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('payroll_runs')
        .update({
          status: 'approved',
          approved_by: userId,
          approved_at: new Date().toISOString()
        })
        .eq('id', payrollRunId)
        .eq('status', 'calculated') // Can only approve calculated payroll

      if (error) throw error

      return { success: true }
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      }
    }
  }

  /**
   * Mark payroll as paid
   */
  static async markPayrollAsPaid(
    payrollRunId: string,
    paymentDate: string,
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Update payroll run
      const { error: runError } = await supabase
        .from('payroll_runs')
        .update({
          status: 'paid',
          payment_date: paymentDate,
          paid_by: userId,
          paid_at: new Date().toISOString()
        })
        .eq('id', payrollRunId)

      if (runError) throw runError

      // Update all payroll details
      const { error: detailsError } = await supabase
        .from('payroll_details')
        .update({
          payment_status: 'paid',
          payment_date: paymentDate
        })
        .eq('payroll_run_id', payrollRunId)

      if (detailsError) throw detailsError

      return { success: true }
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      }
    }
  }
}

export default PayrollService
